import React, { useState, useMemo, useEffect } from 'react';
import { Button } from './button';
import { Dialog, DialogTitle, DialogBody } from './dialog';
import { DocumentTextIcon, ArrowsRightLeftIcon, BanknotesIcon, XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { ref, uploadBytes } from 'firebase/storage';
import { storage } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { logBitacora } from '../utils/bitacora';
import { mutateFianza } from '../utils/contractWrites';
import { parseXmlFianza, parseXmlFactura, validateFianzaFactura } from '../utils/xmlParsers';
import CompactFileReceiver from './CompactFileReceiver';
import {
  ENDOSO_TIPOS,
  EVENTO_TIPO_LABELS,
  ESTADO_FIANZA,
  getEstadoFianza,
  EFECTO_PRIMA,
  EFECTO_PRIMA_LABELS,
  FACTURA_MODES,
  buildEvento,
  buildFactura,
  buildFacturaSustituye,
  applyEndoso,
  ensureFacturas,
  getFacturasVigentes,
  snapshotFromFianza,
  computeCambios,
  formatCambioValor,
  inferEndosoTipos,
  inferEfectoPrima,
  resolvePrimaEndoso,
  resolverEndosoPendiente,
} from '../utils/fianzaEventos';
import { recordComisionEvento } from '../api/comisiones';
import { getPrimaAmount, toTimestamp } from '../utils/calculations';

const fmtMXN = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(n) || 0);

// <input type="date"> round-trip, both halves in LOCAL time.
//
// These used to disagree: toISOString() renders UTC while Date.parse('…T00:00:00')
// reads local. In America/Mexico_City that showed the previous day in the field,
// and merely opening the manual form and saving produced a spurious one-day
// cambio_vigencia — a phantom endoso on a fianza nobody actually changed.
const msToDateInput = (ms) => {
  const n = toTimestamp(ms);
  if (!n) return '';
  const d = new Date(Number(n));
  if (Number.isNaN(d.getTime())) return '';
  const pad = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const dateInputToMs = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  const t = new Date(y, m - 1, d).getTime();
  return Number.isNaN(t) ? null : t;
};

// A small file-drop row: dropzone + selected filename with clear.
function FileSlot({ label, type, file, onFile, disabled }) {
  return (
    <div>
      <div className="text-xs font-medium text-azul-surelia mb-1">{label}</div>
      {file ? (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-cian-digital/40 bg-cian-digital/5 px-3 py-2">
          <span className="text-xs text-gray-700 truncate">{file.name}</span>
          <button type="button" onClick={() => onFile(null)} disabled={disabled} className="text-gray-400 hover:text-red-500 shrink-0">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <CompactFileReceiver
          type={type}
          disabled={disabled}
          onFilesChange={(files) => onFile(files?.[0] || null)}
          placeholder={type === 'xml' ? 'Arrastra o elige XML' : 'Arrastra o elige PDF'}
        />
      )}
    </div>
  );
}

/**
 * EndosoDialog — records a typed endoso on a fianza.
 *
 * Reads the endoso's fianza/factura XML, diffs it against the current fianza to
 * auto-classify the tipos AND the prima effect independently, previews the
 * cambios, appends an event to eventos[] and projects the new values onto the
 * fianza (root + embedded XML) via applyEndoso. Historical documents are never
 * overwritten. (Comisión is booked by the backend ledger, not previewed here.)
 */
export function EndosoDialog({ isOpen, onClose, fianza, contratoData, clientData, user, userData, buildBitacoraMeta, onApplied, origen = 'fianza', contratoDocumentoId = null, endosoRequeridoId = null }) {
  const createdBy = { uid: user?.uid || null, nombre: userData?.nombre || userData?.correo || null };

  // Axis 1 — what changed. Plural: an endoso is routinely an aumento AND a
  // cambio de texto. `tipos[0]` remains the primary label.
  const [tipos, setTipos] = useState([]);
  const [tipoTouched, setTipoTouched] = useState(false);
  // Axis 2 — what the money did. Independent of the tipos, and the ONLY thing
  // that books a ledger movement.
  const [efectoPrima, setEfectoPrima] = useState(EFECTO_PRIMA.NINGUNO);
  const [efectoTouched, setEfectoTouched] = useState(false);
  const [fianzaXmlFile, setFianzaXmlFile] = useState(null);
  const [fianzaPdfFile, setFianzaPdfFile] = useState(null);
  const [facturaXmlFile, setFacturaXmlFile] = useState(null);
  const [facturaPdfFile, setFacturaPdfFile] = useState(null);
  // Reveal the factura dropzones (XML + PDF). Forced on for money endosos.
  const [incluyeFactura, setIncluyeFactura] = useState(false);
  const [parsedFianza, setParsedFianza] = useState(null);
  const [parsedFactura, setParsedFactura] = useState(null);
  const [nota, setNota] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Manual entry is a fallback: it appears automatically when there is no XML to
  // read from, or when the user explicitly opens it to tweak the extracted values.
  const [manualToggle, setManualToggle] = useState(false);
  // Owner-overridable cargo for a reversal, when the default calculation does
  // not match what the afianzadora actually charged back.
  const [cargoOverride, setCargoOverride] = useState('');

  const [form, setForm] = useState(() => ({
    numeroFianza: fianza?.numeroFianza ?? '',
    tipoFianza: fianza?.tipoFianza ?? '',
    montoAfianzado: fianza?.montoAfianzado ?? '',
    // Seed with the prima NETA vigente, not root `monto`: on an emisión-only
    // fianza the root is the gross CFDI total, and resolvePrimaEndoso reads this
    // field as the new net total — a gross seed inflates the diferencia.
    monto: getPrimaAmount(fianza || {}) || '',
    inicioVigenciaFianza: msToDateInput(fianza?.inicioVigenciaFianza),
    finVigenciaFianza: msToDateInput(fianza?.finVigenciaFianza),
  }));

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const resetAndClose = () => {
    setTipos([]);
    setTipoTouched(false);
    setEfectoPrima(EFECTO_PRIMA.NINGUNO);
    setEfectoTouched(false);
    setFianzaXmlFile(null);
    setFianzaPdfFile(null);
    setFacturaXmlFile(null);
    setFacturaPdfFile(null);
    setIncluyeFactura(false);
    setParsedFianza(null);
    setParsedFactura(null);
    setNota('');
    setError(null);
    setCargoOverride('');
    setManualToggle(false);
    onClose();
  };

  const onFianzaXml = async (file) => {
    if (!file) { setFianzaXmlFile(null); setParsedFianza(null); return; }
    setError(null);
    const res = await parseXmlFianza(file);
    if (!res.success) { setError(`XML de fianza inválido: ${res.error}`); return; }
    setParsedFianza(res.data);
    setFianzaXmlFile(file);
    setForm((f) => ({
      ...f,
      numeroFianza: res.data.numeroFianza ?? f.numeroFianza,
      tipoFianza: res.data.tipo ?? f.tipoFianza,
      montoAfianzado: res.data.montoAfianzado ?? f.montoAfianzado,
      inicioVigenciaFianza: msToDateInput(res.data.inicioVigenciaFianza) || f.inicioVigenciaFianza,
      finVigenciaFianza: msToDateInput(res.data.finVigenciaFianza) || f.finVigenciaFianza,
    }));
  };

  const onFacturaXml = async (file) => {
    if (!file) { setFacturaXmlFile(null); setParsedFactura(null); return; }
    setError(null);
    const res = await parseXmlFactura(file);
    if (!res.success) { setError(res.error); return; }
    setParsedFactura(res.data);
    setFacturaXmlFile(file);
    // Prefill the manual total with the NET prima, never the CFDI total — the
    // total carries derechos, gastos and IVA, and comisión is earned on the
    // prima alone. No concepto matched 'prima' → leave the field for the user
    // rather than seeding a gross number that reads as authoritative.
    setForm((f) => ({ ...f, monto: res.data.primas?.prima ?? f.monto }));
  };

  // Whether we have an XML to read the new values from. When we do, that is the
  // source of truth (no form); the manual form is only used when there is no XML
  // or the user explicitly opens it.
  const useXml = Boolean(parsedFianza || parsedFactura);
  const useForm = manualToggle;               // the form shows ONLY when opted into
  const hasDespues = useXml || manualToggle;  // something to compare exists

  const snapshotAntes = useMemo(() => snapshotFromFianza(fianza || {}), [fianza]);

  // The prima the fianza carries today — from the factura ledger when it has one.
  const primaActual = useMemo(() => getPrimaAmount(fianza || {}), [fianza]);

  // Safe to depend on `efectoPrima`: inference reads montoAfianzado and vigencia
  // only, never monto, so this cannot feed back into the auto-classifier.
  const primaEndoso = useMemo(
    () =>
      resolvePrimaEndoso({
        efectoPrima,
        primaActual,
        facturaPrima:
          parsedFactura?.primas?.prima != null ? Number(parsedFactura.primas.prima) : null,
        manualTotal: manualToggle && form.monto !== '' ? Number(form.monto) : null,
      }),
    [efectoPrima, primaActual, parsedFactura, manualToggle, form.monto]
  );

  const snapshotDespues = useMemo(() => {
    // For a money endoso the projected root `monto` is the TOTAL prima after the
    // endoso, not whatever single number was captured. See resolvePrimaEndoso.
    const montoProyectado = primaEndoso ? primaEndoso.montoProyectado : null;

    if (useForm) {
      return {
        numeroFianza: form.numeroFianza || null,
        tipoFianza: form.tipoFianza || null,
        montoAfianzado: form.montoAfianzado === '' ? null : Number(form.montoAfianzado),
        monto: montoProyectado ?? (form.monto === '' ? null : Number(form.monto)),
        inicioVigenciaFianza: dateInputToMs(form.inicioVigenciaFianza),
        finVigenciaFianza: dateInputToMs(form.finVigenciaFianza),
        fiado: parsedFianza?.fiado || fianza?.xmlFianza?.fiado || null,
        beneficiario: parsedFianza?.beneficiario || fianza?.xmlFianza?.beneficiario || null,
      };
    }
    // Derived straight from the dropped XML, falling back to the current values.
    return {
      numeroFianza: parsedFianza?.numeroFianza ?? fianza?.numeroFianza ?? null,
      tipoFianza: parsedFianza?.tipo ?? fianza?.tipoFianza ?? null,
      montoAfianzado: parsedFianza?.montoAfianzado ?? fianza?.montoAfianzado ?? null,
      // With efectoPrima=ninguno nothing was billed, so the prima stands. Fall
      // back to the NET prima vigente (matching snapshotFromFianza) so a
      // documental endoso never diffs net-antes against gross-después.
      monto: montoProyectado ?? (primaActual || fianza?.monto || null),
      inicioVigenciaFianza: parsedFianza?.inicioVigenciaFianza ?? fianza?.inicioVigenciaFianza ?? null,
      finVigenciaFianza: parsedFianza?.finVigenciaFianza ?? fianza?.finVigenciaFianza ?? null,
      fiado: parsedFianza?.fiado ?? fianza?.xmlFianza?.fiado ?? null,
      beneficiario: parsedFianza?.beneficiario ?? fianza?.xmlFianza?.beneficiario ?? null,
    };
  }, [useForm, form, parsedFianza, fianza, primaEndoso, primaActual]);

  // Consistency between the dropped fianza XML and factura XML (same bond, amount,
  // fiado, emisor). Only runs when both are present.
  const fianzaFacturaWarnings = useMemo(
    () => (parsedFianza && parsedFactura ? validateFianzaFactura(parsedFianza, parsedFactura) : []),
    [parsedFianza, parsedFactura]
  );

  const cambios = useMemo(() => computeCambios(snapshotAntes, snapshotDespues), [snapshotAntes, snapshotDespues]);
  const hasFacturaXml = Boolean(parsedFactura || facturaXmlFile);
  const inferredTipos = useMemo(
    () => inferEndosoTipos(snapshotAntes, snapshotDespues),
    [snapshotAntes, snapshotDespues]
  );
  const inferredEfecto = useMemo(
    () => inferEfectoPrima(snapshotAntes, snapshotDespues, { hasFactura: hasFacturaXml }),
    [snapshotAntes, snapshotDespues, hasFacturaXml]
  );
  const autoClassified = (parsedFianza || parsedFactura) && !tipoTouched;

  // Auto-classify from the parsed XML diff, unless the user overrode it. The two
  // axes are inferred and overridden independently.
  useEffect(() => {
    if (autoClassified) setTipos(inferredTipos);
  }, [autoClassified, inferredTipos]);
  useEffect(() => {
    if (!efectoTouched) setEfectoPrima(inferredEfecto);
  }, [efectoTouched, inferredEfecto]);

  const toggleTipo = (t) => {
    setTipoTouched(true);
    setTipos((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };

  // The money axis, not the tipo, decides everything below.
  const primaCambia = efectoPrima !== EFECTO_PRIMA.NINGUNO;
  const monetaryManual = primaCambia && !fianzaXmlFile && !facturaXmlFile;
  // A prima-moving endoso needs a factura, so its dropzones are always shown;
  // others reveal them via the checkbox (or if a factura file was added).
  const showFactura = primaCambia || incluyeFactura || Boolean(facturaXmlFile) || Boolean(facturaPdfFile);
  // Attaching a factura and leaving the effect at "ninguno" would silently drop
  // the money — this is the case the old tipo-driven model got wrong.
  const facturaSinEfecto = hasFacturaXml && !primaCambia;

  // A sustituye against an already-paid base leaves the fiado with a credit at
  // the afianzadora — worth showing before the user commits.
  const vigentesActuales = useMemo(() => getFacturasVigentes(ensureFacturas(fianza || {})), [fianza]);
  const facturasPagadas = vigentesActuales.length > 0 && vigentesActuales.every((f) => f.pagada);
  const saldoPrevisto =
    facturasPagadas && primaEndoso
      ? Math.max(0, primaActual - primaEndoso.facturaPrima)
      : 0;

  const handleSubmit = async () => {
    // Defence in depth behind the FianzaView gate: a terminal fianza is finished
    // and must not accrue further endosos (the backend enforces this too).
    if (getEstadoFianza(fianza) !== ESTADO_FIANZA.ACTIVA) {
      setError('Esta fianza ya no está activa; no admite endosos.');
      return;
    }
    if (!tipos.length) { setError('Selecciona al menos un tipo de endoso.'); return; }
    if (facturaSinEfecto) {
      setError(
        'Adjuntaste una factura pero marcaste que la prima no cambia. Indica si esa ' +
        'factura complementa la cobertura o sustituye a la anterior.'
      );
      return;
    }
    // Guard BEFORE the storage uploads: an endoso whose prima we cannot determine
    // used to fall back to the current prima, booking a factura (and a comisión)
    // for the whole existing amount instead of the difference.
    if (primaCambia && !primaEndoso) {
      setError(
        'Para cobrar una factura necesito la prima: adjunta el XML de la factura, ' +
        'o abre la captura manual y escribe la prima neta después del endoso.'
      );
      return;
    }
    if (efectoPrima === EFECTO_PRIMA.ADICIONAL && primaEndoso.facturaPrima <= 0) {
      setError(
        `Una factura adicional debe subir la prima neta por encima de la actual (${fmtMXN(primaActual)}).`
      );
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const ts = Date.now();
      const eventoId = `evento_${ts}`;
      const basePath = contratoData?.convenioId
        ? `convenios/${contratoData.convenioId}/${contratoData.id}/contrato/fianzas/${fianza.id}`
        : `clientes/${clientData?.id}/${contratoData?.linkId}/contrato/fianzas/${fianza.id}`;

      const filePaths = {};
      const uploads = [];
      const pushUpload = (file, suffix, key) => {
        if (!file) return;
        const p = `${basePath}/${eventoId}_${suffix}`;
        uploads.push(uploadBytes(ref(storage, p), file));
        filePaths[key] = p;
      };
      pushUpload(fianzaXmlFile, 'fianza.xml', 'fianzaXml');
      pushUpload(fianzaPdfFile, 'fianza.pdf', 'fianzaPdf');
      pushUpload(facturaXmlFile, 'factura.xml', 'facturaXml');
      pushUpload(facturaPdfFile, 'factura.pdf', 'facturaPdf');
      await Promise.all(uploads);

      // The factura ledger entry, when the prima moved. An ADICIONAL carries only
      // the difference and accrues on top; a SUSTITUYE carries the new total,
      // replaces the valid set, and inherits the paid state — the afianzadora
      // cancels, reissues, marks it paid, and keeps the remainder as a saldo a
      // favor del fiado.
      const buildFacturaFor = (current) => {
        if (!primaCambia) return null;
        const vigentes = getFacturasVigentes(ensureFacturas(current));
        const facturaComun = {
          facturaId: parsedFactura?.noIdentificacion || parsedFactura?.folio || null,
          monto: primaEndoso.montoProyectado,
          fecha: ts,
          filePaths: {
            ...(filePaths.facturaXml ? { facturaXml: filePaths.facturaXml } : {}),
            ...(filePaths.facturaPdf ? { facturaPdf: filePaths.facturaPdf } : {}),
          },
          xmlFactura: parsedFactura || null,
          eventoId,
          ts,
        };
        return efectoPrima === EFECTO_PRIMA.SUSTITUYE
          ? buildFacturaSustituye({ ...facturaComun, prima: primaEndoso.facturaPrima, supersedes: vigentes })
          : buildFactura({ ...facturaComun, mode: FACTURA_MODES.ADICIONAL, prima: primaEndoso.facturaPrima });
      };

      // `snapshotAntes` has to come from the fianza as it is at write time, not
      // from the copy this dialog rendered minutes ago.
      const buildEventoFor = (current, factura) => buildEvento({
        tipo: tipos[0],
        tipos,
        efectoPrima,
        snapshotAntes: snapshotFromFianza(current),
        snapshotDespues,
        filePaths,
        xmlFianza: parsedFianza || null,
        xmlFactura: parsedFactura || null,
        origen,
        contratoDocumentoId,
        nota: nota.trim() || null,
        createdBy,
        facturaId: factura?.id || null,
        ts,
      });

      if (primaCambia) {
        // Prima-moving endosos go through the backend so the fianza mutation AND
        // the comisión ledger movement are written atomically (Admin SDK). The
        // contract array write happens server-side, inside that transaction.
        const snap = await getDoc(doc(db, 'contracts', fianza.contratoId));
        if (!snap.exists()) throw new Error('Contrato no encontrado.');
        const current = (snap.data().fianzas || []).find((f) => f.id === fianza.id);
        if (!current) throw new Error('Fianza no encontrada en el contrato.');

        const factura = buildFacturaFor(current);
        const eventoAplicado = buildEventoFor(current, factura);
        // Closing the request rides along in the same write, so a pending endoso
        // can never stay open against one that already happened.
        const nextFianza = resolverEndosoPendiente(
          applyEndoso(current, eventoAplicado, { factura }),
          endosoRequeridoId, eventoAplicado.id, ts
        );
        const overrideNum = cargoOverride === '' ? null : Number(cargoOverride);
        await recordComisionEvento({
          contractId: fianza.contratoId,
          fianzaId: fianza.id,
          eventoTipo: 'endoso',
          eventoTipos: tipos,
          efectoPrima,
          eventoId,
          nextFianza,
          factura,
          cargoOverride: Number.isFinite(overrideNum) ? overrideNum : null,
          fecha: ts,
        });
      } else {
        // Pure lifecycle/data endosos never touch the ledger, but they still
        // rewrite fianzas[] — so they go through a transaction, or a concurrent
        // pagada toggle (or another endoso on the same contract) would erase
        // this event entirely.
        await mutateFianza(db, fianza.contratoId, fianza.id, (current) => {
          const evento = buildEventoFor(current, null);
          return resolverEndosoPendiente(
            applyEndoso(current, evento), endosoRequeridoId, evento.id, ts
          );
        });
      }

      try {
        const label = tipos.map((t) => EVENTO_TIPO_LABELS[t] || t).join(' + ');
        const efectoLabel = primaCambia ? `, ${EFECTO_PRIMA_LABELS[efectoPrima]}` : '';
        const numero = fianza.numeroFianza || 'sin número';
        await logBitacora({
          action: 'UPDATE',
          collection: 'contracts',
          documentId: fianza.contratoId,
          user,
          userData,
          metadata: buildBitacoraMeta
            ? buildBitacoraMeta(
                `Registró endoso (${label}${efectoLabel}) en fianza #${numero}`,
                { endosoTipos: tipos, efectoPrima, eventoId }
              )
            : {
                contratoId: fianza.contratoId,
                fianzaId: fianza.id,
                endosoTipos: tipos,
                efectoPrima,
                description: `Registró endoso (${label}${efectoLabel}) en fianza #${numero}`,
              },
        });
      } catch (e) {
        console.error('bitacora endoso failed', e);
      }

      onApplied?.();
      resetAndClose();
    } catch (e) {
      console.error('endoso failed', e);
      setError(e.message || 'No se pudo registrar el endoso.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={submitting ? () => {} : resetAndClose} size="2xl">
      <DialogTitle>
        <span className="text-azul-surelia">Registrar endoso</span>
      </DialogTitle>
      <DialogBody>
        <div className="space-y-4">
          {/* Documentos del endoso */}
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-center gap-2 mb-3">
              <DocumentTextIcon className="w-4 h-4 text-azul-surelia" />
              <h4 className="text-sm font-semibold text-gray-800">Documentos del endoso</h4>
              <span className="text-xs text-gray-400">Suelta el XML: clasificamos el endoso y extraemos los valores automáticamente</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FileSlot label="XML fianza" type="xml" file={fianzaXmlFile} onFile={onFianzaXml} disabled={submitting} />
              <FileSlot label="PDF fianza" type="pdf" file={fianzaPdfFile} onFile={setFianzaPdfFile} disabled={submitting} />
            </div>

            {/* Factura dropzones — always when the prima moves, opt-in otherwise */}
            {!primaCambia && (
              <label className="flex items-center gap-2 mt-3 text-xs text-gray-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showFactura}
                  onChange={(e) => setIncluyeFactura(e.target.checked)}
                  disabled={submitting}
                />
                La factura también cambia (adjuntar XML y PDF de la factura)
              </label>
            )}
            {showFactura && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                <FileSlot label="XML factura" type="xml" file={facturaXmlFile} onFile={onFacturaXml} disabled={submitting} />
                <FileSlot label="PDF factura" type="pdf" file={facturaPdfFile} onFile={setFacturaPdfFile} disabled={submitting} />
              </div>
            )}
            {error && (
              <div className="mt-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</div>
            )}
            {fianzaFacturaWarnings.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {fianzaFacturaWarnings.map((w, i) => (
                  <div
                    key={i}
                    className={`rounded-lg border px-3 py-2 text-xs ${
                      w.type === 'critical'
                        ? 'bg-red-50 border-red-200 text-red-700'
                        : 'bg-yellow-50 border-yellow-300 text-yellow-800'
                    }`}
                  >
                    <span className="font-semibold">
                      {w.type === 'critical' ? 'Inconsistencia' : 'Advertencia'}: {w.field}.
                    </span>{' '}
                    {w.message}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tipo (auto-classified, editable) */}
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <ArrowsRightLeftIcon className="w-4 h-4 text-azul-surelia" />
                <h4 className="text-sm font-semibold text-gray-800">Tipo de endoso</h4>
              </div>
              {autoClassified && tipos.length > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-verde-lima/20 text-azul-surelia text-[11px] font-semibold px-2 py-0.5">
                  <SparklesIcon className="w-3 h-3" /> Detectado del XML
                </span>
              )}
            </div>
            {/* Multi-select: one endoso is often an aumento AND a cambio de texto. */}
            <p className="text-[11px] text-gray-500 mb-2">
              Marca todo lo que cambió. Un endoso puede ser varias cosas a la vez.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {ENDOSO_TIPOS.map((t) => (
                <label
                  key={t}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition-colors ${
                    tipos.includes(t)
                      ? 'border-azul-surelia bg-azul-surelia/5 text-azul-surelia'
                      : 'border-gray-300 text-gray-700 hover:border-azul-surelia/40'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={tipos.includes(t)}
                    onChange={() => toggleTipo(t)}
                    className="rounded border-gray-300 text-azul-surelia focus:ring-azul-surelia"
                  />
                  {EVENTO_TIPO_LABELS[t]}
                </label>
              ))}
            </div>

            {/* The money axis, independent of the tipos above. */}
            <div className="mt-4 pt-3 border-t border-gray-100">
              <div className="flex items-center gap-2 mb-1">
                <BanknotesIcon className="w-4 h-4 text-azul-surelia" />
                <h4 className="text-sm font-semibold text-gray-800">Efecto en la prima</h4>
              </div>
              <p className="text-[11px] text-gray-500 mb-2">
                Independiente del tipo: ampliar la vigencia de una fianza multianual cobra
                prima extra aunque el monto afianzado no se mueva.
              </p>
              <div className="space-y-1.5">
                {Object.values(EFECTO_PRIMA).map((ef) => (
                  <label
                    key={ef}
                    className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition-colors ${
                      efectoPrima === ef
                        ? 'border-azul-surelia bg-azul-surelia/5'
                        : 'border-gray-300 hover:border-azul-surelia/40'
                    }`}
                  >
                    <input
                      type="radio"
                      name="efectoPrima"
                      checked={efectoPrima === ef}
                      onChange={() => { setEfectoPrima(ef); setEfectoTouched(true); }}
                      className="mt-0.5 border-gray-300 text-azul-surelia focus:ring-azul-surelia"
                    />
                    <span className={efectoPrima === ef ? 'text-azul-surelia' : 'text-gray-700'}>
                      {EFECTO_PRIMA_LABELS[ef]}
                    </span>
                  </label>
                ))}
              </div>

              {facturaSinEfecto && (
                <div className="mt-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800">
                  Adjuntaste una factura pero marcaste que la prima no cambia. Indica si
                  complementa la cobertura o si sustituye a la factura anterior — si no,
                  ese dinero no se registra.
                </div>
              )}

              {efectoPrima === EFECTO_PRIMA.SUSTITUYE && facturasPagadas && (
                <div className="mt-2 rounded-lg bg-cian-digital/10 border border-cian-digital/30 px-3 py-2 text-xs text-azul-surelia">
                  La factura anterior ya está pagada. La afianzadora la cancela y reexpide
                  marcándola como pagada; la diferencia
                  {saldoPrevisto > 0 ? ` (${fmtMXN(saldoPrevisto)})` : ''} queda como saldo
                  a favor del fiado.
                </div>
              )}
            </div>

            {monetaryManual && (
              <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                Cuando la prima cambia conviene adjuntar el XML del endoso (fianza y factura): así prima, tarifa y comisión se recalculan con datos reales. Sin XML, abre la captura manual y escribe la prima neta después del endoso.
              </div>
            )}
          </div>

          {/* Valores — taken from the XML by default; manual entry is the fallback */}
          {manualToggle ? (
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <BanknotesIcon className="w-4 h-4 text-azul-surelia" />
                  <h4 className="text-sm font-semibold text-gray-800">Valores</h4>
                </div>
                <button type="button" onClick={() => setManualToggle(false)} className="text-xs text-cian-digital hover:underline">
                  {useXml ? 'Usar valores del XML' : 'Cancelar'}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Monto afianzado</label>
                  <input
                    type="number"
                    value={form.montoAfianzado}
                    onChange={(e) => setField('montoAfianzado', e.target.value)}
                    className={`w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-1 ${primaCambia ? 'border-verde-lima focus:border-verde-lima focus:ring-verde-lima' : 'border-gray-300 focus:border-azul-surelia focus:ring-azul-surelia'}`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {primaCambia ? 'Prima neta después del endoso' : 'Prima (neta)'}
                  </label>
                  {primaCambia && (
                    <p className="text-[11px] text-gray-500 mb-1">
                      Prima actual: {fmtMXN(primaActual)}
                      {efectoPrima === EFECTO_PRIMA.ADICIONAL && primaEndoso
                        ? ` · diferencia a facturar: ${fmtMXN(primaEndoso.facturaPrima)}`
                        : ''}
                    </p>
                  )}
                  <input type="number" value={form.monto} onChange={(e) => setField('monto', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Vigencia inicio</label>
                  <input type="date" value={form.inicioVigenciaFianza} onChange={(e) => setField('inicioVigenciaFianza', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Vigencia fin</label>
                  <input type="date" value={form.finVigenciaFianza} onChange={(e) => setField('finVigenciaFianza', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia" />
                </div>
              </div>
            </div>
          ) : useXml ? (
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <SparklesIcon className="w-4 h-4 text-verde-lima shrink-0" />
                Valores tomados del XML. Revisa la comparación abajo.
              </div>
              <button type="button" onClick={() => setManualToggle(true)} className="text-xs text-cian-digital hover:underline shrink-0">
                Ajustar manualmente
              </button>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-3 flex items-center justify-between gap-2 text-xs text-gray-500">
              <span>Suelta el XML arriba y extraemos los valores. ¿Sin XML?</span>
              <button type="button" onClick={() => setManualToggle(true)} className="text-cian-digital hover:underline shrink-0">
                Ingresar manualmente
              </button>
            </div>
          )}

          {/* Nota (always available) */}
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">Nota (opcional)</label>
            <textarea value={nota} onChange={(e) => setNota(e.target.value)} rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia"
              placeholder="Contexto del endoso…" />
          </div>

          {/* Disminución — carta de crédito (fiado) + cargo override (agente) */}
          {efectoPrima === EFECTO_PRIMA.SUSTITUYE && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <BanknotesIcon className="w-4 h-4 text-amber-700" />
                <h4 className="text-sm font-semibold text-amber-900">Reexpedición — saldo y comisión</h4>
              </div>
              <p className="text-[11px] text-amber-800">
                Si el fiado ya pagó, la afianzadora reexpide la factura marcándola pagada y le deja la diferencia como saldo a favor. La comisión ya cobrada se convierte en adeudo con la afianzadora (se compensa contra comisiones futuras); si estaba pendiente, solo se ajusta.
              </p>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-xs font-medium text-amber-900 mb-1">
                    Cargo a comisión (opcional — deja vacío para el cálculo automático)
                  </label>
                  <input
                    type="number"
                    value={cargoOverride}
                    onChange={(e) => setCargoOverride(e.target.value)}
                    placeholder="Monto que descuenta la afianzadora"
                    disabled={submitting}
                    className="w-full rounded-lg border border-amber-300 px-3 py-2 text-sm outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                  />
                  <p className="text-[10px] text-amber-700 mt-1">
                    Ingresa un valor solo si la afianzadora descuenta una cifra distinta a la del cálculo.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Preview: comparación general — only once there is something to compare.
              (The comisión preview card was dropped: pre-tarifa it only said
              "Por definir → Por definir"; the ledger/Reportes are the source.) */}
          {hasDespues && (
          <div className="rounded-xl border border-gray-200 bg-zinc-50 p-3">
            <div className="text-xs font-semibold text-gray-700 mb-2">Comparación (antes → después)</div>
            {cambios.length === 0 ? (
              <div className="text-xs text-gray-500">Sin cambios en los datos (endoso documental).</div>
            ) : (
              <ul className="space-y-1">
                {cambios.map((c) => (
                  <li key={c.campo} className="text-xs text-gray-700 flex flex-wrap items-center gap-1">
                    <span className="font-medium">{c.label}:</span>
                    <span className="text-gray-400 line-through">{formatCambioValor(c.campo, c.valorAnterior)}</span>
                    <span className="text-gray-300">→</span>
                    <span className="text-azul-surelia font-semibold">{formatCambioValor(c.campo, c.valorNuevo)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button plain onClick={resetAndClose} disabled={submitting}>Cancelar</Button>
            <Button color="surelia" onClick={handleSubmit} disabled={submitting || !tipos.length || facturaSinEfecto}>
              {submitting ? 'Registrando…' : 'Registrar endoso'}
            </Button>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}

export default EndosoDialog;
